export type ClientAnalyticsEvent =
  | 'app_opened' | 'play_selected' | 'context_selected' | 'interaction_selected'
  | 'share_opened' | 'share_sent' | 'challenge_opened' | 'answer_submitted'
  | 'another_game_selected' | 'new_challenge_created' | 'session_abandoned'
  | 'socket_disconnected' | 'recovery_attempted' | 'recovery_succeeded'
  | 'snapshot_recovery_used' | 'recovery_failed' | 'answer_ack_timeout'
  | 'unrecoverable_session' | 'group_replay_started'
  | 'connection_opened' | 'play_again_selected' | 'relationship_label_set' | 'relationship_label_changed'
  | 'insight_viewed'
  | 'replay_video_requested' | 'replay_video_shared'
  | 'arcade_opened' | 'arcade_game_started' | 'arcade_game_completed' | 'arcade_challenge_created' | 'arcade_challenge_opened' | 'arcade_challenge_beaten';

interface QueuedEvent {
  eventId: string;
  eventName: ClientAnalyticsEvent;
  occurredAt: string;
  properties?: Record<string, string | number | boolean>;
}

let authInitData: string | null = null;
let clientSessionId = crypto.randomUUID();
let queue: QueuedEvent[] = [];
let flushTimer: number | undefined;
let flushing = false;
let retryDelayMs = 400;

const scheduleFlush = (delayMs = 400): void => {
  if (flushTimer !== undefined) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = undefined;
    void flushAnalytics();
  }, delayMs);
};

export const trackAnalytics = (
  event: ClientAnalyticsEvent,
  properties?: Record<string, string | number | boolean>
): void => {
  if (!authInitData) return;
  queue.push({ eventId: crypto.randomUUID(), eventName: event, occurredAt: new Date().toISOString(), properties });
  if (queue.length > 100) queue = queue.slice(-100);
  scheduleFlush();
};

export const flushAnalytics = async (): Promise<void> => {
  if (!authInitData || flushing || queue.length === 0) return;
  flushing = true;
  const batch = queue.slice(0, 20);
  try {
    const response = await fetch('/analytics/events', {
      method: 'POST',
      cache: 'no-store',
      keepalive: true,
      headers: {
        Accept: 'application/json', Authorization: `tma ${authInitData}`,
        'Content-Type': 'application/json', 'X-UTAGE-Protocol': '1'
      },
      body: JSON.stringify({ clientSessionId, events: batch })
    });
    if (!response.ok) throw new Error(`Analytics request failed (${response.status}).`);
    queue.splice(0, batch.length);
    retryDelayMs = 400;
  } catch (error) {
    retryDelayMs = Math.min(Math.max(retryDelayMs * 2, 2_000), 30_000);
    if (import.meta.env.DEV) console.warn('UTAGE analytics delivery deferred.', error);
  } finally {
    flushing = false;
    if (queue.length > 0) scheduleFlush(retryDelayMs);
  }
};

export const initializeAnalytics = (initData: string, properties: Record<string, string | number | boolean>): (() => void) => {
  authInitData = initData;
  clientSessionId = crypto.randomUUID();
  retryDelayMs = 400;
  trackAnalytics('app_opened', properties);
  const onPageHide = (): void => { void flushAnalytics(); };
  const onVisible = (): void => { if (document.visibilityState === 'visible') void flushAnalytics(); };
  window.addEventListener('pagehide', onPageHide);
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    window.removeEventListener('pagehide', onPageHide);
    document.removeEventListener('visibilitychange', onVisible);
    void flushAnalytics();
    authInitData = null;
  };
};
